/* eslint-disable react/no-unescaped-entities */
import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
    ShieldCheck, Zap, Target, Crown, Heart, Users,
    Sparkles, Check, Plus, Loader2,
} from 'lucide-react';

// Packs d'objectifs curatés par Homly : bundles de KPIs transverses (plusieurs familles)
// orientés vers un objectif business concret. Activables en 1 clic.
const PACKS = [
    {
        id: 'reduce-fallthrough',
        name: 'Réduire le taux de chute',
        desc: "Sécurisez vos ventes, du compromis jusqu'à l'acte authentique.",
        icon: ShieldCheck,
        color: '#EF4444',
        kpis: ['compromis_fallthrough', 'total_fallthrough', 'financing_fallthrough', 'compromis_fallthrough_reason', 'compromis_risk', 'compromis_to_acte'],
    },
    {
        id: 'accelerate-sales',
        name: 'Accélérer les ventes',
        desc: 'Raccourcissez les délais et boostez la transfo visite → offre → vente.',
        icon: Zap,
        color: '#FF914D',
        kpis: ['time_to_market', 'time_to_sell', 'mandate_to_sale_rate', 'visit_to_offer_rate', 'visits_per_offer', 'closings_this_month', 'ca_forecast_m1'],
    },
    {
        id: 'reliable-estimation',
        name: "Fiabiliser l'estimation",
        desc: 'Gagnez en précision de prix et en conversion estimation → mandat.',
        icon: Target,
        color: '#3B82F6',
        kpis: ['estim_precision', 'estim_to_mandate_rate', 'estim_overestim_rate', 'estim_render_delay', 'estim_top_objections'],
    },
    {
        id: 'win-exclusivity',
        name: 'Gagner des mandats exclusifs',
        desc: 'Construisez un portefeuille de qualité, plus rentable et maîtrisé.',
        icon: Crown,
        color: '#A855F7',
        kpis: ['exclusivity_rate', 'mandates_signed', 'lead_to_mandate_rate', 'estim_to_mandate_rate', 'avg_fees_pct', 'mandate_sign_first_rdv'],
    },
    {
        id: 'retain-sellers',
        name: 'Fidéliser & rassurer les vendeurs',
        desc: 'Détectez les vendeurs à risque et renforcez leur satisfaction.',
        icon: Heart,
        color: '#10B981',
        kpis: ['seller_risk_red_zone', 'seller_no_contact_14d', 'seller_avg_rating', 'report_frequency', 'mandates_no_contact_7d', 'seller_sensitive_topics_30d'],
    },
    {
        id: 'pilot-team',
        name: "Piloter la performance de l'équipe",
        desc: 'Objectifs, classement et risques de sous-performance, par agent.',
        icon: Users,
        color: '#6366F1',
        kpis: ['agent_ranking', 'agent_ca_vs_target', 'monthly_target_achievement', 'agent_consistency', 'agent_underperform_risk', 'agent_radar_profile'],
    },
];

function PackCard({ pack, allKpisById, favoriteIds, onActivate }) {
    const [activating, setActivating] = useState(false);
    const Icon = pack.icon;

    const validIds = useMemo(
        () => pack.kpis.filter((id) => allKpisById[id]),
        [pack.kpis, allKpisById]
    );
    const followed = validIds.filter((id) => favoriteIds.includes(id)).length;
    const allActive = validIds.length > 0 && followed === validIds.length;

    const handleClick = async () => {
        if (allActive || activating) return;
        setActivating(true);
        try {
            await onActivate(pack, validIds);
        } finally {
            setActivating(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.35 }}
            className="flex flex-col p-4 rounded-xl border border-white/[0.07] bg-white/[0.015] hover:bg-white/[0.03] transition-all"
            data-testid={`objective-pack-${pack.id}`}
        >
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${pack.color}1f` }}>
                        <Icon className="w-4.5 h-4.5" style={{ color: pack.color }} />
                    </div>
                    <h3 className="text-sm font-bold text-foreground leading-tight">{pack.name}</h3>
                </div>
                <span className="text-[10px] font-bold text-muted-foreground/70 shrink-0 mt-1">{validIds.length} KPI</span>
            </div>

            <p className="text-[11px] text-muted-foreground/80 leading-relaxed mb-3">{pack.desc}</p>

            <div className="flex flex-wrap gap-1 mb-3">
                {validIds.slice(0, 4).map((id) => (
                    <span key={id} className="px-1.5 py-0.5 rounded text-[9.5px] bg-white/[0.04] text-foreground/70 border border-white/[0.05]">
                        {allKpisById[id]?.name}
                    </span>
                ))}
                {validIds.length > 4 && (
                    <span className="px-1.5 py-0.5 rounded text-[9.5px] text-muted-foreground/60">+{validIds.length - 4}</span>
                )}
            </div>

            <div className="mt-auto flex items-center justify-between gap-2 pt-2 border-t border-white/[0.05]">
                <span className="text-[10px] text-muted-foreground/70 font-medium">
                    {followed}/{validIds.length} suivis
                </span>
                <button
                    onClick={handleClick}
                    disabled={allActive || activating}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold flex items-center gap-1.5 transition-all ${
                        allActive
                            ? 'bg-green-500/15 text-green-400 cursor-default'
                            : 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white hover:opacity-90'
                    }`}
                    data-testid={`activate-pack-${pack.id}`}
                >
                    {activating ? (
                        <><Loader2 className="w-3 h-3 animate-spin" /> Activation…</>
                    ) : allActive ? (
                        <><Check className="w-3 h-3" /> Pack actif</>
                    ) : (
                        <><Plus className="w-3 h-3" /> Activer le pack</>
                    )}
                </button>
            </div>
        </motion.div>
    );
}

export default function KPIObjectivePacks({ allKpisById, favoriteIds, onActivate }) {
    return (
        <div
            className="rounded-2xl bg-gradient-to-br from-[#681793]/[0.05] via-transparent to-[#FF914D]/[0.05] border border-white/[0.06] p-5"
            data-testid="objective-packs-section"
        >
            <div className="flex items-center gap-2.5 mb-1">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center shadow-lg shadow-[#681793]/15">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
                <div>
                    <h2 className="text-sm font-black text-foreground flex items-center gap-2">
                        Packs d'objectifs
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-[#FF914D]/15 text-[#FF914D] border border-[#FF914D]/20">
                            Recommandé par Homly
                        </span>
                    </h2>
                    <p className="text-[11px] text-muted-foreground/80">
                        Activez en 1 clic un ensemble de KPIs cohérents pour piloter un objectif concret.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                {PACKS.map((pack) => (
                    <PackCard
                        key={pack.id}
                        pack={pack}
                        allKpisById={allKpisById}
                        favoriteIds={favoriteIds}
                        onActivate={onActivate}
                    />
                ))}
            </div>
        </div>
    );
}
